// Author: Ahsan Saleem
/**
 * The object resolver — continuous flow.
 *
 * A related-object link no longer navigates you away: it RESOLVES the target object
 * and opens it in the Inspector, on top of whatever you were looking at. Drilling
 * Zone → Sensor → Alert → back to Zone never costs you the screen behind (the
 * Inspector's back-stack walks the trail). Any object addressable from the live
 * facility snapshot is registered here; `ObjectRef` consults this registry, and
 * falls back to route navigation for kinds not yet resolvable.
 *
 * These views read the shared, self-refreshing snapshot (SystemProvider), so an
 * opened object is itself live — its numbers tick through <LiveNumber>.
 */
import type { ReactNode } from 'react';
import { Thermometer, Droplets, Wind, Sun, FlaskConical, Zap, Gauge, MapPin, Radio, TriangleAlert } from 'lucide-react';
import { auth, getTrends, metricSuffix, zoneOrdinalOf, readingInZone, type Latest, type Alert, type Zone } from '../api.js';
import { useAsync } from '../hooks.js';
import { useSystem } from '../ui/system.js';
import { Provenance, StatusDot, EmptyState } from '../ui/primitives.js';
import { Sparkline } from '../ui/charts.js';
import type { Pt } from '../analytics.js';
import { ObjectView } from './ui/ObjectView.js';
import { ObjectRef } from './ui/primitives.js';
import { LiveNumber, Freshness } from './ui/live.js';
import { ObjectInsights } from './ai.js';

/* ── resolver plumbing ───────────────────────────────────────────────────── */
interface Resolved { title: ReactNode; node: ReactNode; }
interface ObjectKind { icon: typeof MapPin; label: string; render: (ref: string) => ReactNode; }

const REGISTRY: Record<string, ObjectKind> = {
  zone: { icon: MapPin, label: 'Zone', render: (ref) => <ZoneObject zoneRef={ref} /> },
  datapoint: { icon: Radio, label: 'Sensor', render: (ref) => <DatapointObject code={ref} /> },
  alert: { icon: TriangleAlert, label: 'Alert', render: (ref) => <AlertObject id={ref} /> },
};

export function hasResolver(kind?: string): boolean { return !!kind && kind in REGISTRY; }

export function resolveObject(kind: string, ref: string): Resolved | null {
  const k = REGISTRY[kind];
  if (!k) return null;
  const Icon = k.icon;
  return { title: <span className="insp-ztitle"><Icon size={15} aria-hidden /> {k.label}</span>, node: k.render(ref) };
}

/** Open a registered object into the Inspector (a push onto its back-stack). */
export function openObject(inspector: { open: (t: ReactNode, n: ReactNode) => void }, kind: string, ref: string): boolean {
  const r = resolveObject(kind, ref);
  if (!r) return false;
  inspector.open(r.title, r.node);
  return true;
}

/* ── shared bits ─────────────────────────────────────────────────────────── */
const DP_ICON: Array<[RegExp, typeof Thermometer]> = [
  [/temp/i, Thermometer], [/humid|rh/i, Droplets], [/co2|air\.flow|wind/i, Wind],
  [/light|par|lux/i, Sun], [/ph\b/i, FlaskConical], [/ec\b|conduct|volt|current|power|pump/i, Zap],
];
export const iconFor = (code: string) => (DP_ICON.find(([re]) => re.test(code))?.[1]) ?? Gauge;
export const decimalsFor = (code: string) => (/humid|rh|co2|ppm|lux|par/i.test(code) ? 0 : 1);
/** Display symbol for a unit code — UCUM 'Cel' → '°C'; unknown units pass through. */
const UNIT_LABEL: Record<string, string> = { Cel: '°C', '[degF]': '°F', '[pH]': 'pH' };
export const unitLabel = (unit: string | null | undefined): string => (unit ? UNIT_LABEL[unit] ?? unit : '');
/** An alert still needing attention. Matches the server's "open" definition
 *  (reports.ts dailyBrief) — a RECOVERED/CLOSED alert is resolved, not attention. */
const OPEN_ALERT_STATES = new Set(['OPEN', 'ACKNOWLEDGED', 'INVESTIGATING']);
export const isOpenAlert = (a: Alert): boolean => OPEN_ALERT_STATES.has((a.state ?? '').toUpperCase());
/** Health tone for a zone from its alerts + stale sensors. */
export function zoneHealth(alerts: Alert[], staleCount: number): 'critical' | 'caution' | 'success' {
  if (alerts.some((a) => a.severity?.toUpperCase() === 'CRITICAL')) return 'critical';
  if (alerts.length > 0 || staleCount > 0) return 'caution';
  return 'success';
}
const zoneOf = (snapZones: Zone[], r: Latest) =>
  snapZones.find((z) => (r.zoneId != null && z.id === r.zoneId) || zoneOrdinalOf(r.datapointCode) === z.ordinal);

function Missing({ what }: { what: string }) {
  return <div className="obj-missing"><EmptyState icon={<Radio size={22} />} title={`${what} not on this snapshot`}>It may have cleared or belongs to another facility.</EmptyState></div>;
}

/* ── Zone ────────────────────────────────────────────────────────────────── */
function ZoneObject({ zoneRef }: { zoneRef: string }) {
  const { snapshot } = useSystem();
  if (!snapshot) return <div className="obj-loading" aria-busy />;
  const zone = snapshot.zones.find((z) => z.id === zoneRef || z.key === zoneRef);
  if (!zone) return <Missing what="Zone" />;
  const solo = snapshot.zones.length === 1 ? snapshot.zones[0].id : null;
  const readings = snapshot.latest.filter((l) => readingInZone(l, zone, solo));
  const alerts = snapshot.alerts.filter((a) => a.zone_id === zone.id);
  const stale = readings.filter((r) => r.stale).length;
  return (
    <ObjectView
      kind="Zone" reference={zone.name} title={zone.name}
      facts={[
        { k: 'Sensors', v: <LiveNumber value={readings.length} /> },
        { k: 'Open alerts', v: <LiveNumber value={alerts.length} /> },
        { k: 'Stale', v: <LiveNumber value={stale} /> },
        { k: 'Ordinal', v: `#${zone.ordinal}` },
      ]}
      related={alerts.length
        ? <>{alerts.map((a) => <ObjectRef key={a.id} objectKind="alert" objectRef={a.id} kind="Alert" label={a.rule_name} icon={TriangleAlert} />)}</>
        : <span className="obj-quiet">No open alerts in this zone.</span>}
    >
      <ObjectInsights kind="zone" objectRef={zone.id} snapshot={snapshot} />
      <section className="v2-section">
        <div className="v2-section-h">Live readings</div>
        {readings.length === 0 ? <div className="obj-quiet">No sensors reporting.</div> : (
          <div className="v2-related">
            {readings.map((r) => (
              <ObjectRef key={r.datapointCode} objectKind="datapoint" objectRef={r.datapointCode}
                kind={metricSuffix(r.datapointCode)} icon={iconFor(r.datapointCode)}
                label={<span className="obj-read-v"><LiveNumber value={r.value} decimals={decimalsFor(r.datapointCode)} />{r.unit ? ` ${unitLabel(r.unit)}` : ''}{r.stale && <span className="obj-stale"> · stale</span>}</span>} />
            ))}
          </div>
        )}
      </section>
    </ObjectView>
  );
}

/* ── Sensor / datapoint ──────────────────────────────────────────────────── */
function DatapointObject({ code }: { code: string }) {
  const { snapshot } = useSystem();
  const siteId = auth.siteId!;
  const trend = useAsync(() => getTrends(siteId, code, isoDaysAgo(7), isoNow(), 'day'), [code]);
  if (!snapshot) return <div className="obj-loading" aria-busy />;
  const r = snapshot.latest.find((l) => l.datapointCode === code);
  if (!r) return <Missing what="Sensor" />;
  const zone = zoneOf(snapshot.zones, r);
  const relatedAlerts = snapshot.alerts.filter((a) => metricSuffix(a.metric) === metricSuffix(code));
  const pts: Pt[] = (trend.data?.points ?? []).filter((p) => p.avg != null).map((p) => ({ t: new Date(p.bucket).getTime(), v: p.avg as number }));
  const dec = decimalsFor(code);
  return (
    <ObjectView
      kind="Sensor" reference={metricSuffix(code)} title={r.name ?? metricSuffix(code)}
      statusType="quality" status={r.stale ? 'STALE' : r.quality}
      provenance={<Provenance source={r.source} />}
      facts={[
        { k: 'Reading', v: <span className="obj-big"><LiveNumber value={r.value} decimals={dec} />{r.unit ? ` ${unitLabel(r.unit)}` : ''}</span> },
        { k: 'Updated', v: r.observedAt ? <Freshness at={new Date(r.observedAt).getTime()} prefix="" /> : '—' },
        { k: 'Zone', v: zone?.name ?? '—' },
        { k: 'Channel', v: <code className="obj-code">{code}</code> },
      ]}
      related={<>
        {zone && <ObjectRef objectKind="zone" objectRef={zone.id} kind="Zone" label={zone.name} icon={MapPin} />}
        {relatedAlerts.map((a) => <ObjectRef key={a.id} objectKind="alert" objectRef={a.id} kind="Alert" label={a.rule_name} icon={TriangleAlert} />)}
      </>}
    >
      <ObjectInsights kind="datapoint" objectRef={code} snapshot={snapshot} />
      <section className="v2-section">
        <div className="v2-section-h">7-day trend</div>
        {pts.length >= 2 ? <div className="obj-spark"><Sparkline points={pts} height={54} /></div> : <div className="obj-quiet">Not enough history to trend yet.</div>}
      </section>
    </ObjectView>
  );
}

/* ── Alert ───────────────────────────────────────────────────────────────── */
function AlertObject({ id }: { id: string }) {
  const { snapshot } = useSystem();
  if (!snapshot) return <div className="obj-loading" aria-busy />;
  const a: Alert | undefined = snapshot.alerts.find((x) => x.id === id);
  if (!a) return <Missing what="Alert" />;
  const zone = a.zone_id ? snapshot.zones.find((z) => z.id === a.zone_id) : undefined;
  const dp = snapshot.latest.find((l) => metricSuffix(l.datapointCode) === metricSuffix(a.metric)
    && ((a.zone_id && l.zoneId === a.zone_id) || (zone && zoneOrdinalOf(l.datapointCode) === zone.ordinal)));
  const sev = a.severity?.toUpperCase();
  const tone = sev === 'CRITICAL' ? 'critical' : sev === 'WARNING' ? 'caution' : 'info';
  return (
    <ObjectView
      kind="Alert" reference={a.rule_name} title={a.rule_name}
      statusType="alert" status={a.state}
      exception={a.state?.toUpperCase() === 'OPEN' ? {
        tone, title: `${sev ?? 'Alert'} — ${metricSuffix(a.metric)}`,
        detail: a.opened_value != null ? `Tripped at ${a.opened_value}` : undefined,
        action: dp ? <ObjectRef objectKind="datapoint" objectRef={dp.datapointCode} kind="Sensor" label="Open the sensor" icon={Radio} /> : undefined,
      } : undefined}
      facts={[
        { k: 'Severity', v: <StatusDot tone={tone} label={sev ?? '—'} /> },
        { k: 'Metric', v: metricSuffix(a.metric) },
        { k: 'Tripped at', v: a.opened_value != null ? <span className="tnum">{a.opened_value}</span> : '—' },
        { k: 'Opened', v: a.opened_at ? <Freshness at={new Date(a.opened_at).getTime()} prefix="" /> : '—' },
      ]}
      related={<>
        {dp && <ObjectRef objectKind="datapoint" objectRef={dp.datapointCode} kind="Sensor" label={metricSuffix(a.metric)} icon={iconFor(a.metric)} />}
        {zone && <ObjectRef objectKind="zone" objectRef={zone.id} kind="Zone" label={zone.name} icon={MapPin} />}
      </>}
    >
      <ObjectInsights kind="alert" objectRef={a.id} snapshot={snapshot} />
      {!dp && !zone && <div className="obj-quiet">No linked sensor or zone on this snapshot.</div>}
    </ObjectView>
  );
}

const isoNow = () => new Date().toISOString();
const isoDaysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString();
